import { useEffect, useState, useRef, FC } from 'react';
import { SignatureStatus } from 'rlv-common';
import LoadingSpinner from '@eg/elements/LoadingSpinner';
import { CheckColorIcon } from '@eg/elements/components/Icons';
import { CancelSignModal } from '../../../../components/CancelSignModal/CancelSignModal';
import { TrackingElementSigningPage } from '../../../../tracking/tracking.types';
import { SignatureErrorModal } from '../../../../components/SignatureErrorModal/SignatureErrorModal';
import AemFragment from '../../../../components/AemFragment/AemFragment';
import Footer from '../../../../components/Footer';
import { Headline } from '../../../../components/Headline/Headline';
import Text from '../../../../components/Text';
import { NavigationAction, StateName } from '../../../../routing/StateMachineTypes';
import { Tracker } from '../../../../tracking/tracker';
import { AemFragmentCatalog } from '../../../../types/AemFragmentCatalog';
import { getSignature } from '../../../../services/api';
import './POSAGSignature.css';
import { POSAGSignatureIframe } from './PSAGSignatureIframe';
import { POSAGSignatureWindow } from './PSAGSignatureWindow';

const INTERVAL_DELAY = 3000;
const USE_WINDOW_FOR_SIGNING = true; // if set to false iframe will be used, this has potential for future refactoring and dynamic switch between 2 signing options

interface POSAGSignatureProps {
  businessId: string;
  onNext: (value: string, navigationRequest: NavigationAction) => void;
  onGoBack: () => void;
}

const tracker = Tracker.getInstance();

export const POSAGSignature: FC<POSAGSignatureProps> = ({ businessId, onGoBack, onNext }) => {
  const [completedSign, setCompletedSign] = useState(false);
  const [zurukModal, setZurukModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [iFrameUrl, setIFrameUrl] = useState<string | undefined>();
  const intervalId = useRef<ReturnType<typeof setInterval>>();
  const semaphore = useRef<boolean>(); // some backend request take longer than the interval. We want to avoid several request in the air at the same time.

  const getSignatureState = (fresh: boolean = false) => {
    if (!completedSign && !semaphore.current) {
      semaphore.current = true;
      getSignature(businessId, fresh)
        .then(res => {
          if (res.signatureStatus === SignatureStatus.FETCHED || res.signatureStatus === SignatureStatus.FETCH_FAILED) {
            console.log('Signature completion confirmed: ', res?.signatureStatus);
            tracker.trackClickEvent({ clickedElement: TrackingElementSigningPage.DIGITAL_SIGNATURE_ENTERED_SUCCESSFULLY });
            setCompletedSign(true);
            clearInterval(intervalId.current);
          }
          setIFrameUrl(res.signingUrl);
          semaphore.current = false;
        })
        .catch(() => {
          setErrorModal(true);
        });
    }
  };

  const backHandler = (event: Event) => {
    setZurukModal(true);
    semaphore.current = true;
    event.preventDefault();
  };

  useEffect(() => {
    getSignatureState(true);
    clearInterval(intervalId.current);
    intervalId.current = setInterval(getSignatureState, INTERVAL_DELAY);
    addEventListener('popstate', backHandler);
    return () => {
      clearInterval(intervalId.current);
      removeEventListener('popstate', backHandler);
    };
  }, []);

  const onCloseModal = () => {
    tracker.trackClickEvent({
      clickedElement: TrackingElementSigningPage.BUTTON_ONLINE_ABSCHLUSS_FORTSETZEN,
    });
    setZurukModal(false);
    semaphore.current = false;
  };

  const onDismissModal = () => {
    tracker.trackClickEvent({
      clickedElement: TrackingElementSigningPage.ICON_SCHLIESSENX,
    });
    setZurukModal(false);
    semaphore.current = false;
  };

  const openModal = () => {
    setZurukModal(true);
    semaphore.current = true;
  };

  const handleSignError = () => {
    setErrorModal(true);
    semaphore.current = true;
  };

  return (
    <div className="posagsignature content-container">
      <Headline text={'Unterschreiben Sie Ihren Antrag jetzt online.'} />
      <Text>
        Mit Ihren Unterschriften nehmen Sie die Vertragsinhalte, die Hinweise über die Folgen einer
        Verletzung der vorvertraglichen Anzeigepflicht nach § 19 Abs. 5 VVG und die Belehrung zum
        Widerrufsrecht zur Kenntnis.
      </Text>
      <Text>
        Sie bestätigen zudem, dass Ihre Angaben richtig und vollständig sind und geben die
        Erklärungen in den heruntergeladenen Unterlagen zum Abschluss dieser Versicherung, zum
        SEPA-Lastschriftmandat, zum Datenschutz und zur Empfangsbestätigung ab.
      </Text>
      <Text styles="undertext">
        Wenn Sie Ihre Unterschrift digital leisten, speichert ERGO das digitale Abbild Ihrer
        Unterschrift als zusätzliche Antragsdaten.
      </Text>

      <div className="signing-container">
        <LoadingSpinner show={!completedSign && !iFrameUrl && !errorModal && !zurukModal} viewport="relative" />
        {!completedSign && iFrameUrl && (
          <>
            {USE_WINDOW_FOR_SIGNING && (
              <POSAGSignatureWindow
                url={iFrameUrl}
                onError={handleSignError}
              />
            )}
            {!USE_WINDOW_FOR_SIGNING && (
              <POSAGSignatureIframe
                url={iFrameUrl}
                onError={handleSignError}
              />
            )}
            <AemFragment name={AemFragmentCatalog.STEP23VA} useVA={true} />
          </>
        )}
        {completedSign && (
          <div className="signature-succeed">
            <CheckColorIcon width="30" height="30" />
            <Text styles="">
              <b>Vielen Dank für Ihre Unterschriften!</b> Ihre Unterschriften wurden ins
              Antragsdokument übertragen
            </Text>
          </div>
        )}
      </div>

      <Footer
        labelNext="weiter zum Online-Antrag"
        handleNext={() => {
          onNext('', NavigationAction.NEXT);
        }}
        handleAction={(navigationAction: NavigationAction) => undefined}
        showNext={true}
        showPrevious={true}
        disableNext={!completedSign}
        handleBack={openModal}
      />

      <SignatureErrorModal
        open={errorModal}
        onClick={() => {
          setErrorModal(false);
          semaphore.current = false;
        }}
      />
      <CancelSignModal
        open={zurukModal}
        onCloseModal={onCloseModal}
        onGoBack={onGoBack}
        onDismissModal={onDismissModal}
      />
    </div>
  );
};
